import React, { useState, useEffect, useRef } from 'react';
import { pazientiAPI } from '../../services/api';
import '../../styles/NotaInline.css';
import '../../styles/NotaInlineConsole.css';

/**
 * NotaInline
 * Testo del paziente editabile sul posto. Salva automaticamente quando il
 * campo perde il focus, ma solo se il testo e' cambiato: nessuna chiamata
 * inutile al server.
 *
 * SERVE A DUE CAMPI, NON SOLO ALLA NOTA
 * La nota del paziente e i medici di riferimento si comportano allo stesso
 * modo: si scrivono in riga, si salvano uscendo dal campo, dicono
 * "salvato". `salva` decide dove finiscono. Una seconda copia di questo
 * componente per il secondo campo avrebbe fatto divergere due cose che
 * devono comportarsi identiche - a partire dal "salva solo se e'
 * cambiato", che e' la parte che nessuno riscrive uguale.
 *
 * Props:
 *  - pazienteId
 *  - valoreIniziale
 *  - compatta      variante stretta per la cella della lista
 *  - salva         (id, testo) => Promise. Default: la nota del paziente
 *  - placeholder   testo del campo vuoto
 *  - righe         altezza in righe
 *  - onSaved(nuovoTesto)   callback opzionale dopo il salvataggio
 */
const NotaInline = ({
    pazienteId, valoreIniziale = '', compatta = false, onSaved,
    salva = pazientiAPI.updateNote,
    placeholder = 'Aggiungi una nota…',
    righe,
}) => {
    const [testo, setTesto] = useState(valoreIniziale || '');
    const [salvato, setSalvato] = useState(valoreIniziale || '');
    const [stato, setStato] = useState('idle'); // idle | saving | ok | error
    const timerRef = useRef(null);

    useEffect(() => {
        setTesto(valoreIniziale || '');
        setSalvato(valoreIniziale || '');
    }, [valoreIniziale, pazienteId]);

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const salvaSeCambiato = async () => {
        if (testo === salvato) return; // niente da salvare
        try {
            setStato('saving');
            await salva(pazienteId, testo);
            setSalvato(testo);
            setStato('ok');
            if (onSaved) onSaved(testo);
            timerRef.current = setTimeout(() => setStato('idle'), 1500);
        } catch (e) {
            console.error('Errore salvataggio nota:', e);
            setStato('error');
        }
    };

    // Evita che il click nella cella della lista faccia navigare alla scheda
    const stopClick = (e) => e.stopPropagation();

    return (
        <div className={`nota-inline ${compatta ? 'compatta' : ''}`} onClick={stopClick}>
            <textarea
                className="nota-inline-campo"
                value={testo}
                placeholder={placeholder}
                rows={righe ?? (compatta ? 1 : 2)}
                onClick={stopClick}
                onChange={(e) => setTesto(e.target.value)}
                onBlur={salvaSeCambiato}
            />
            {stato === 'saving' && <span className="nota-inline-stato salvando">salvataggio…</span>}
            {stato === 'ok' && <span className="nota-inline-stato ok">salvato ✓</span>}
            {stato === 'error' && <span className="nota-inline-stato errore">errore, riprova</span>}
        </div>
    );
};

export default NotaInline;
